import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, SendIcon, CheckCircle2Icon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
const CITIES = ['Alpine', 'American Fork', 'Cedar Hills', 'Draper', 'Eagle Mountain', 'Herriman', 'Highland', 'Layton', 'Lehi', 'Lindon', 'Mapleton', 'Murray', 'Orem', 'Payson', 'Pleasant Grove', 'Provo', 'Riverton', 'Salem', 'Salt Lake City', 'Sandy', 'Saratoga Springs', 'South Jordan', 'Spanish Fork', 'Springville', 'Vineyard', 'West Jordan', 'West Valley City', 'Other'];
const SERVICES = ['Residential Moving', 'Specialty Item Moving', 'Storage Cleanout', 'Junk Removal', 'Other'];
const SPACE_TYPES = ['Studio', '1 Bedroom', '2 Bedroom', '3 Bedroom', '4+ Bedroom', 'Single Family Home', 'Townhome', 'Storage Unit', 'Office', 'Other'];
const PROPERTY_ACCESS = ['Ground Floor / Easy Access', 'Stairs (1 flight)', 'Stairs (2+ flights)', 'Elevator', 'Long Walk from Parking'];
const SPECIALTY_ITEMS = ['Piano', 'Pool Table', 'Safe / Gun Safe', 'Large Appliances (Fridge, Washer, etc.)', 'Exercise Equipment', 'Artwork / Antiques', 'Hot Tub', 'Glass Furniture'];
const PACKING_OPTIONS = ["None. I'll pack everything myself", 'Partial. Help with fragile items only', 'Full. Pack everything for me'];
const TIME_PREFERENCES = ['Morning (8am – 12pm)', 'Afternoon (12pm – 5pm)', 'Evening (5pm – 8pm)', 'Flexible'];
const FLEXIBILITY_OPTIONS = ['Locked in. This exact date', 'Flexible by a few days', 'Very flexible. Find me the best slot'];
const HEARD_FROM = ['Google Search', 'Friend or Family', 'Social Media', 'Yard Sign / Truck', 'Other'];
const CONTACT_METHODS = ['Email', 'Phone Call', 'Text Message'];
interface FormData {
  // Contact
  name: string;
  email: string;
  phone: string;
  preferredContact: string;
  // From location
  fromCity: string;
  fromSpaceType: string;
  fromAccess: string;
  // To location
  toCity: string;
  toSpaceType: string;
  toAccess: string;
  // What's being moved
  service: string;
  squareFeet: string;
  bigItems: string;
  specialtyItems: string[];
  // Services
  packing: string;
  disassembly: string;
  needSupplies: string;
  // Scheduling
  preferredDate: string;
  timePreference: string;
  flexibility: string;
  // Other
  heardFrom: string;
  notes: string;
}
export function QuotePage() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    preferredContact: '',
    fromCity: '',
    fromSpaceType: '',
    fromAccess: '',
    toCity: '',
    toSpaceType: '',
    toAccess: '',
    service: '',
    squareFeet: '',
    bigItems: '',
    specialtyItems: [],
    packing: '',
    disassembly: '',
    needSupplies: '',
    preferredDate: '',
    timePreference: '',
    flexibility: '',
    heardFrom: '',
    notes: ''
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value
    });
  };
  const toggleSpecialtyItem = (item: string) => {
    setForm(prev => ({
      ...prev,
      specialtyItems: prev.specialtyItems.includes(item) ? prev.specialtyItems.filter(i => i !== item) : [...prev.specialtyItems, item]
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const response = await fetch('https://formspree.io/f/xjgzqlwz', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          ...form,
          specialtyItems: form.specialtyItems.join(', ') || 'None',
          _subject: `New Quote Request from ${form.name}`
        })
      });
      if (response.ok) {
        setSubmitted(true);
      } else {
        const data = await response.json().catch(() => null);
        setError(data?.errors?.[0]?.message || 'Something went wrong. Please try again or email us directly at team@justicemovingco.com.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again, or email us at team@justicemovingco.com.');
    } finally {
      setSubmitting(false);
    }
  };
  const inputStyles = 'w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy transition-colors';
  const labelStyles = 'block text-sm font-semibold text-navy mb-1.5';
  const selectStyles = 'w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-navy focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy transition-colors appearance-none';
  const SectionHeading = ({
    children
  }: {
    children: React.ReactNode;
  }) => <h3 className="text-lg font-bold text-navy mb-4 pb-2 border-b border-gray-100" data-id="element-125">
      {children}
    </h3>;
  const SelectField = ({
    id,
    label,
    required,
    value,
    options,
    placeholder
  }: {
    id: keyof FormData;
    label: string;
    required?: boolean;
    value: string;
    options: string[];
    placeholder: string;
  }) => <div data-id="element-126">
      <label htmlFor={id} className={labelStyles} data-id="element-127">
        {label} {required && '*'}
      </label>
      <div className="relative" data-id="element-128">
        <select id={id} name={id} required={required} value={value} onChange={handleChange} className={selectStyles} data-id="element-129">
          <option value="" disabled data-id="element-130">
            {placeholder}
          </option>
          {options.map(o => <option key={o} value={o} data-id="element-131">
              {o}
            </option>)}
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3" data-id="element-132">
          <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" data-id="element-133">
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" data-id="element-134" />
          </svg>
        </div>
      </div>
    </div>;
  if (submitted) {
    return <section className="py-24 bg-gray-50 min-h-[60vh] flex items-center" data-id="element-135">
        <div className="max-w-lg mx-auto px-4 text-center" data-id="element-136">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.4
        }} data-id="element-137">
            <div className="w-20 h-20 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-138">
              <CheckCircle2Icon className="h-10 w-10 text-gold" data-id="element-139" />
            </div>
            <h2 className="text-3xl font-bold text-navy mb-4" data-id="element-140">
              Quote Request Sent!
            </h2>
            <p className="text-gray-600 mb-8" data-id="element-141">
              Thanks for the detailed info. We'll review everything and send
              your personalized estimate within 24 hours. No follow-up call
              required.
            </p>
            <Button onClick={() => navigate('/')} variant="secondary" data-id="element-142">
              <ArrowLeftIcon className="mr-2 h-4 w-4" data-id="element-143" />
              Back to Home
            </Button>
          </motion.div>
        </div>
      </section>;
  }
  return <section className="py-16 bg-gray-50 min-h-[60vh]" data-id="element-144">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-145">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="mb-8" data-id="element-146">
          <button onClick={() => navigate('/')} className="inline-flex items-center text-sm text-gray-500 hover:text-navy transition-colors mb-6" data-id="element-147">
            <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-148" />
            Back to Home
          </button>
          <h1 className="text-3xl md:text-4xl font-bold text-navy mb-3" data-id="element-149">
            Get Your Free Quote
          </h1>
          <p className="text-gray-600 text-lg" data-id="element-150">
            The more we know, the more accurate your estimate. Fill out the
            details below and we'll send your personalized quote within 24
            hours.
          </p>
        </motion.div>

        {/* Form */}
        <motion.form initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 0.1
      }} onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-10 space-y-8" data-id="element-151">
          {/* Contact Info */}
          <div data-id="element-152">
            <SectionHeading data-id="element-153">Contact Information</SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-154">
              <div data-id="element-155">
                <label htmlFor="name" className={labelStyles} data-id="element-156">
                  Full Name *
                </label>
                <input id="name" name="name" type="text" required placeholder="John Smith" value={form.name} onChange={handleChange} className={inputStyles} data-id="element-157" />
              </div>
              <div data-id="element-158">
                <label htmlFor="email" className={labelStyles} data-id="element-159">
                  Email Address *
                </label>
                <input id="email" name="email" type="email" required placeholder="john@example.com" value={form.email} onChange={handleChange} className={inputStyles} data-id="element-160" />
              </div>
              <div data-id="element-161">
                <label htmlFor="phone" className={labelStyles} data-id="element-162">
                  Phone Number *
                </label>
                <input id="phone" name="phone" type="tel" required placeholder="(801) 555-0123" value={form.phone} onChange={handleChange} className={inputStyles} data-id="element-163" />
              </div>
              <SelectField id="preferredContact" label="Preferred Contact Method" required value={form.preferredContact} options={CONTACT_METHODS} placeholder="How should we reach you?" data-id="element-164" />
            </div>
          </div>

          {/* Move From */}
          <div data-id="element-165">
            <SectionHeading data-id="element-166">Moving From</SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-167">
              <SelectField id="fromCity" label="City" required value={form.fromCity} options={CITIES} placeholder="Select origin city" data-id="element-168" />
              <SelectField id="fromSpaceType" label="Type of Space" required value={form.fromSpaceType} options={SPACE_TYPES} placeholder="Select space type" data-id="element-169" />
              <div className="md:col-span-2" data-id="element-170">
                <SelectField id="fromAccess" label="Property Access" required value={form.fromAccess} options={PROPERTY_ACCESS} placeholder="How easy is access?" data-id="element-171" />
              </div>
            </div>
          </div>

          {/* Move To */}
          <div data-id="element-172">
            <SectionHeading data-id="element-173">Moving To</SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-174">
              <SelectField id="toCity" label="City" required value={form.toCity} options={CITIES} placeholder="Select destination city" data-id="element-175" />
              <SelectField id="toSpaceType" label="Type of Space" required value={form.toSpaceType} options={SPACE_TYPES} placeholder="Select space type" data-id="element-176" />
              <div className="md:col-span-2" data-id="element-177">
                <SelectField id="toAccess" label="Property Access" required value={form.toAccess} options={PROPERTY_ACCESS} placeholder="How easy is access?" data-id="element-178" />
              </div>
            </div>
          </div>

          {/* What's Being Moved */}
          <div data-id="element-179">
            <SectionHeading data-id="element-180">What You're Moving</SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5" data-id="element-181">
              <SelectField id="service" label="Service Type" required value={form.service} options={SERVICES} placeholder="Select a service" data-id="element-182" />
              <div data-id="element-183">
                <label htmlFor="squareFeet" className={labelStyles} data-id="element-184">
                  Approx. Square Footage
                </label>
                <input id="squareFeet" name="squareFeet" type="number" min="0" placeholder="e.g. 1200" value={form.squareFeet} onChange={handleChange} className={inputStyles} data-id="element-185" />
              </div>
              <div className="md:col-span-2" data-id="element-186">
                <label htmlFor="bigItems" className={labelStyles} data-id="element-187">
                  # of Large Items (couches, dressers, appliances, etc.)
                </label>
                <input id="bigItems" name="bigItems" type="number" min="0" placeholder="e.g. 8" value={form.bigItems} onChange={handleChange} className={inputStyles} data-id="element-188" />
              </div>
            </div>

            {/* Specialty Items Checklist */}
            <div data-id="element-189">
              <label className={labelStyles} data-id="element-190">
                Do you have any specialty items?{' '}
                <span className="text-gray-400 font-normal" data-id="element-191">
                  (check all that apply)
                </span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-192">
                {SPECIALTY_ITEMS.map(item => {
                const checked = form.specialtyItems.includes(item);
                return <button key={item} type="button" onClick={() => toggleSpecialtyItem(item)} className={`flex items-center gap-3 px-4 py-3 rounded-lg border text-left text-sm transition-colors ${checked ? 'bg-navy/5 border-navy text-navy font-medium' : 'bg-white border-gray-200 text-gray-600 hover:border-navy/30'}`} data-id="element-193">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${checked ? 'bg-navy border-navy' : 'border-gray-300'}`} data-id="element-194">
                        {checked && <CheckCircle2Icon className="h-4 w-4 text-white" data-id="element-195" />}
                      </div>
                      {item}
                    </button>;
              })}
              </div>
            </div>
          </div>

          {/* Additional Services */}
          <div data-id="element-196">
            <SectionHeading data-id="element-197">Additional Services</SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-198">
              <SelectField id="packing" label="Packing Help" value={form.packing} options={PACKING_OPTIONS} placeholder="Need packing services?" data-id="element-199" />
              <SelectField id="disassembly" label="Furniture Disassembly / Reassembly" value={form.disassembly} options={['Yes', 'No', 'Not sure yet']} placeholder="Will you need this?" data-id="element-200" />
              <div className="md:col-span-2" data-id="element-201">
                <SelectField id="needSupplies" label="Moving Supplies (boxes, wrap, tape)" value={form.needSupplies} options={['Yes, please include in quote', 'No, I have my own', 'Maybe']} placeholder="Need supplies?" data-id="element-202" />
              </div>
            </div>
          </div>

          {/* Scheduling */}
          <div data-id="element-203">
            <SectionHeading data-id="element-204">Scheduling</SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-205">
              <div data-id="element-206">
                <label htmlFor="preferredDate" className={labelStyles} data-id="element-207">
                  Preferred Move Date *
                </label>
                <input id="preferredDate" name="preferredDate" type="date" required value={form.preferredDate} onChange={handleChange} min={new Date().toISOString().split('T')[0]} className={inputStyles} data-id="element-208" />
              </div>
              <SelectField id="timePreference" label="Preferred Time" value={form.timePreference} options={TIME_PREFERENCES} placeholder="When works best?" data-id="element-209" />
              <div className="md:col-span-2" data-id="element-210">
                <SelectField id="flexibility" label="How Flexible Is Your Date?" value={form.flexibility} options={FLEXIBILITY_OPTIONS} placeholder="Select your flexibility" data-id="element-211" />
              </div>
            </div>
          </div>

          {/* Other */}
          <div data-id="element-212">
            <SectionHeading data-id="element-213">A Few Last Things</SectionHeading>
            <div className="space-y-5" data-id="element-214">
              <SelectField id="heardFrom" label="How Did You Hear About Us?" value={form.heardFrom} options={HEARD_FROM} placeholder="Let us know" data-id="element-215" />
              <div data-id="element-216">
                <label htmlFor="notes" className={labelStyles} data-id="element-217">
                  Anything Else We Should Know?
                </label>
                <textarea id="notes" name="notes" rows={4} placeholder="Pets, narrow doorways, parking restrictions, items that need extra care, timing concerns. The more detail, the better." value={form.notes} onChange={handleChange} className={`${inputStyles} resize-none`} data-id="element-218" />
              </div>
            </div>
          </div>

          {/* Error message */}
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm" data-id="element-219">
              {error}
            </div>}

          {/* Submit */}
          <div className="pt-2" data-id="element-220">
            <Button type="submit" variant="primary" disabled={submitting} className="w-full text-lg py-4 disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-221">
              {submitting ? 'Sending...' : <>
                  Submit Request
                  <SendIcon className="ml-2 h-5 w-5" data-id="element-222" />
                </>}
            </Button>
          </div>
        </motion.form>
      </div>
    </section>;
}